import { externalJobsService } from '@/services/external-jobs.service'
import type {
  ExternalJobMatchRequest,
  ExternalJobSearchParams,
  JobApplyResumeStatus,
} from '@/types'
import {
  type Query,
  useInfiniteQuery,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'

const PAGE_SIZE = 20

// Query key factory for external jobs
export const externalJobsKeys = {
  all: ['external-jobs'] as const,
  lists: () => [...externalJobsKeys.all, 'list'] as const,
  list: (params: ExternalJobSearchParams) => [...externalJobsKeys.lists(), params] as const,
  details: () => [...externalJobsKeys.all, 'detail'] as const,
  detail: (id: number) => [...externalJobsKeys.details(), id] as const,
  recommended: () => [...externalJobsKeys.all, 'recommended'] as const,
  categories: () => [...externalJobsKeys.all, 'categories'] as const,
  matches: () => [...externalJobsKeys.all, 'matches'] as const,
  applyResume: (jobId: number) => [...externalJobsKeys.all, 'apply-resume', jobId] as const,
}

export function useExternalJobs(params: ExternalJobSearchParams = {}) {
  return useQuery({
    queryKey: externalJobsKeys.list(params),
    queryFn: () => externalJobsService.searchJobs(params),
  })
}

export function useInfiniteExternalJobs(
  params: Omit<ExternalJobSearchParams, 'skip' | 'limit'> = {},
) {
  return useInfiniteQuery({
    queryKey: [...externalJobsKeys.lists(), 'infinite', params],
    queryFn: ({ pageParam = 0 }) =>
      externalJobsService.searchJobs({
        ...params,
        skip: pageParam,
        limit: PAGE_SIZE,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      // If we got fewer items than requested, there are no more
      if (lastPage.items.length < PAGE_SIZE) {
        return undefined
      }
      // Return the next skip value
      return allPages.reduce((total, page) => total + page.items.length, 0)
    },
  })
}

export function useExternalJob(jobId: number) {
  return useQuery({
    queryKey: externalJobsKeys.detail(jobId),
    queryFn: () => externalJobsService.getJob(jobId),
    enabled: !!jobId,
  })
}

export function useRecommendedJobs(limit = 10) {
  return useQuery({
    queryKey: externalJobsKeys.recommended(),
    queryFn: () => externalJobsService.getRecommendedJobs(limit),
  })
}

export function useJobCategories() {
  return useQuery({
    queryKey: externalJobsKeys.categories(),
    queryFn: () => externalJobsService.getCategories(),
    staleTime: 1000 * 60 * 60, // Cache for 1 hour
  })
}

export function useAnalyzeJobMatch() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      jobId,
      request,
      chatId,
    }: {
      jobId: number
      request?: ExternalJobMatchRequest
      chatId?: string | null
    }) => externalJobsService.analyzeMatch(jobId, request, chatId),
    onSuccess: () => {
      // Invalidate matches and recommended jobs
      queryClient.invalidateQueries({ queryKey: externalJobsKeys.matches() })
      queryClient.invalidateQueries({ queryKey: externalJobsKeys.recommended() })
    },
  })
}

export function useMyJobMatches(skip = 0, limit = 20) {
  return useQuery({
    queryKey: [...externalJobsKeys.matches(), skip, limit],
    queryFn: () => externalJobsService.getMyMatches(skip, limit),
  })
}

/** Apply-resume runs that should poll until terminal state. */
const APPLY_RESUME_ACTIVE_STATUSES = new Set(['preparing', 'generating_revision', 'reanalyzing'])

export function useApplyResumeStatus(jobId: number | null) {
  return useQuery<JobApplyResumeStatus | null>({
    queryKey: externalJobsKeys.applyResume(jobId || 0),
    queryFn: () => externalJobsService.getApplyResumeStatus(jobId || 0),
    enabled: Boolean(jobId),
    retry: false,
  })
}

const APPLY_RESUME_STALE_MS = 5 * 60 * 1000

/**
 * Fetch apply-resume run status for many jobs (cards persist after refresh).
 * Polls every 2s while status is preparing / generating_revision / reanalyzing.
 */
export function useApplyResumeStatusesForJobs(jobIds: number[]) {
  const ids = [...new Set(jobIds.filter((id) => Number.isFinite(id) && id > 0))]

  const queries = useQueries({
    queries: ids.map((jobId) => ({
      queryKey: externalJobsKeys.applyResume(jobId),
      queryFn: () => externalJobsService.getApplyResumeStatus(jobId),
      enabled: jobId > 0,
      staleTime: APPLY_RESUME_STALE_MS,
      retry: false,
      refetchInterval: (query: Query<JobApplyResumeStatus | null, Error>) => {
        const status = query.state.data?.status
        return status && APPLY_RESUME_ACTIVE_STATUSES.has(status) ? 2000 : false
      },
    })),
  })

  const runsByJobId: Record<number, JobApplyResumeStatus> = {}
  for (let i = 0; i < ids.length; i++) {
    const jobId = ids[i] as number
    const data = queries[i]?.data
    if (data) {
      runsByJobId[jobId] = data
    }
  }

  return { runsByJobId, queries, jobIds: ids }
}

export function useStartApplyResume() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (jobId: number) => externalJobsService.startApplyResume(jobId),
    onSuccess: (data) => {
      queryClient.setQueryData(externalJobsKeys.applyResume(data.job_listing_id), data)
      void queryClient.invalidateQueries({
        queryKey: externalJobsKeys.applyResume(data.job_listing_id),
      })
    },
  })
}

export function useRetryApplyResume() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (jobId: number) => externalJobsService.retryApplyResume(jobId),
    onSuccess: (data) => {
      queryClient.setQueryData(externalJobsKeys.applyResume(data.job_listing_id), data)
      void queryClient.invalidateQueries({
        queryKey: externalJobsKeys.applyResume(data.job_listing_id),
      })
    },
  })
}
